// doctor-frontend/src/pages/intern/perf/PerformanceReviewQueue.jsx
// SR → Faculty performance review queue.
// No-regression patch:
// - Preserves existing internApi.perfReviewerQueue() workflow and route placement.
// - Adds tenant slug fallback from storage so navigation keeps /t/:slug after redirects.
// - Adds Core Practice Area-safe display sanitization.
// - Handles multiple backend list response shapes without changing backend contracts.
// - Adds status aliases for submitted/returned/finalized performance reviews.
// - Keeps performance review, guided activity, and citizen OPD visit workflows separate.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { internApi } from '../../../services/internApi';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const STATUSES = [
  { value: 'sr-draft', label: 'SR Draft' },
  { value: 'sr-submitted,submitted-to-faculty,faculty-review,faculty-pending', label: 'Submitted to Faculty' },
  { value: 'returned,returned-to-sr', label: 'Returned by Faculty' },
  { value: 'faculty-final,faculty-finalized,finalized,approved', label: 'Faculty Finalized' },
  {
    value: 'sr-submitted,submitted-to-faculty,faculty-review,faculty-pending,returned,returned-to-sr',
    label: 'Pending (Submitted/Returned)',
  },
];

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function coerceList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.reviews)) return payload.reviews;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.queue)) return payload.queue;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
}

function persistTenantSlug(slug) {
  try {
    const clean = String(slug || '').trim();
    if (!clean) return;
    localStorage.setItem('tenantSlug', clean);
    localStorage.setItem('activeTenantSlug', clean);
    sessionStorage.setItem('tenantSlug', clean);
    sessionStorage.setItem('activeTenantSlug', clean);
  } catch {
    // ignore storage failures
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
      safeJsonParse(localStorage.getItem('authUser')),
      safeJsonParse(sessionStorage.getItem('authUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore
  }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

/** Same tenantUrl style used across intern pages; keeps /t/:slug and prevents double prefixing. */
function tenantUrl(path) {
  if (!path) return path;
  const s = String(path);

  if (/^https?:\/\//i.test(s)) return s;

  const p = s.startsWith('/') ? s : `/${s}`;
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    if (m) {
      const prefix = m[0];
      const slug = prefix.replace(/^\/t\//, '');
      persistTenantSlug(slug);
      if (!p.startsWith(`${prefix}/`)) return `${prefix}${p}`.replace(/\/{2,}/g, '/');
      return p;
    }
  } catch {
    // fall through to storage fallback
  }

  const slug = readTenantSlugFromStorage();
  if (!slug) return p;
  const prefix = `/t/${slug}`;
  if (p.startsWith(`${prefix}/`)) return p;
  return `${prefix}${p}`.replace(/\/{2,}/g, '/');
}

function iso(d) {
  try {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return new Date().toISOString().slice(0, 10);
    return dt.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function normalizeStatusToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');
}

function statusLabel(value) {
  const s = normalizeStatusToken(value);
  if (s === 'sr-draft') return 'SR Draft';
  if (['sr-submitted', 'submitted-to-faculty', 'faculty-review', 'faculty-pending'].includes(s)) {
    return 'Submitted to Faculty';
  }
  if (s === 'returned' || s === 'returned-to-sr') return 'Returned by Faculty';
  if (['faculty-final', 'faculty-finalized', 'finalized', 'approved'].includes(s)) return 'Faculty Finalized';
  return sanitizeDisplayText(value) || '-';
}

function statusChipColor(value) {
  const s = normalizeStatusToken(value);
  if (s.includes('final') || s === 'approved') return 'success';
  if (s.includes('submit') || s.includes('pending') || s.includes('review')) return 'warning';
  if (s.includes('return')) return 'default';
  if (s.includes('draft')) return 'info';
  return 'default';
}
function personLabel(value) {
  if (!value) return '';
  if (typeof value === 'string') return sanitizeDisplayText(value);
  if (typeof value === 'object') {
    return sanitizeDisplayText(
      value.name ||
        value.fullName ||
        value.displayName ||
        value.email ||
        value.username ||
        value.userId ||
        value._id ||
        value.id ||
        ''
    );
  }
  return sanitizeDisplayText(value);
}

function dateLabel(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return sanitizeDisplayText(value);
    return d.toDateString();
  } catch {
    return sanitizeDisplayText(value);
  }
}

function avgLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '–';
  return n.toFixed(1);
}

function truncateText(value, limit = 110) {
  const text = sanitizeDisplayText(value || '');
  if (!text) return '—';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1)}…`;
}

function reviewId(item = {}) {
  return item._id || item.id || item.reviewId || item.performanceReviewId || '';
}

function internName(item = {}) {
  return personLabel(
    item.internName ||
      item.internUser ||
      item.internUserId ||
      item.internId ||
      item.userName ||
      item.userEmail ||
      '—'
  );
}

function srReviewerName(item = {}) {
  return personLabel(item.srReviewer || item.srReviewerName || item.srReviewerId || '');
}

function facultyReviewerName(item = {}) {
  return personLabel(item.facultyReviewer || item.facultyReviewerName || item.facultyReviewerId || '');
}

function competencyCount(item = {}) {
  if (Array.isArray(item.competencies)) return item.competencies.length;
  if (Array.isArray(item.scores)) return item.scores.length;
  return 0;
}

function normalizeReview(item = {}) {
  const source = item && typeof item === 'object' ? item : {};
  const id = reviewId(source);
  const notes = sanitizeDisplayText(source.summaryNotes || source.notes || source.reviewSummary || '');
  const status = source.status || source.workflowStatus || source.reviewStatus || '';

  return {
    ...source,
    _safeId: id,
    _safeStatus: status,
    _safeStatusLabel: statusLabel(status),
    _safeInternName: internName(source),
    _safeSrReviewer: srReviewerName(source),
    _safeFacultyReviewer: facultyReviewerName(source),
    _safePeriodStart: source.periodStart || source.from || source.startDate || '',
    _safePeriodEnd: source.periodEnd || source.to || source.endDate || '',
    _safeAvg: avgLabel(source.avg ?? source.average ?? source.averageScore),
    _safeSummary: truncateText(notes, 130),
    _safeCompetencyCount: competencyCount(source),
  };
}

export default function PerformanceReviewQueue() {
  const navigate = useNavigate();
  const location = useLocation();

  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
  const [status, setStatus] = useState('sr-submitted,submitted-to-faculty,faculty-review,faculty-pending,returned,returned-to-sr');
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const returnTo = useMemo(() => {
    try {
      return `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    } catch {
      return '';
    }
  }, [location.pathname, location.search, location.hash]);

  const hasBadDates = useMemo(() => {
    if (!from || !to) return false;
    return new Date(from) > new Date(to);
  }, [from, to]);

  const statusCounts = useMemo(() => {
    const counts = { submitted: 0, returned: 0, finalized: 0, draft: 0 };
    items.forEach((item) => {
      const s = normalizeStatusToken(item._safeStatus);
      if (s.includes('return')) counts.returned += 1;
      else if (s.includes('final') || s === 'approved') counts.finalized += 1;
      else if (s.includes('draft')) counts.draft += 1;
      else if (s.includes('submit') || s.includes('pending') || s.includes('review')) counts.submitted += 1;
    });
    return counts;
  }, [items]);

  const load = useCallback(async () => {
    if (hasBadDates) {
      setErr('End date must be on or after Start date.');
      return;
    }

    setLoading(true);
    setErr('');
    try {
      const res = await internApi.perfReviewerQueue({
        from,
        to,
        status,
        page: 1,
        limit: 100,
        cb: Date.now(),
      });
      setItems(coerceList(res).map(normalizeReview));
    } catch (e) {
      setErr(e?.message || 'Failed to load queue');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, status, hasBadDates]);

  useEffect(() => {
    load();
  }, [load]);

  const queuePath = tenantUrl('/intern/perf/review');
  const reviewerWorkbenchPath = tenantUrl('/dashboard/approval');
  const activityLogPath = tenantUrl('/intern/activity/log');
  const activityLauncherPath = tenantUrl('/intern/activity/new');

  function openReview(item) {
    const id = item?._safeId;
    if (!id) {
      setErr('Cannot open review: missing review ID.');
      return;
    }

    navigate(tenantUrl(`/intern/perf/review/${encodeURIComponent(id)}`), {
      state: { from: returnTo || queuePath },
    });
  }

  return (
    <Box className="space-y-4 p-4 md:p-6">
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h5" className="font-semibold">
            Performance Review Queue
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
            Review SR-submitted intern performance assessments here. This workflow is kept separate from guided activity evidence and citizen OPD visit workflow.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || hasBadDates}>
            Refresh
          </Button>
          <Button size="small" variant="outlined" component={Link} to={reviewerWorkbenchPath} startIcon={<LaunchIcon />}>
            Reviewer Workbench
          </Button>
          <Button size="small" variant="outlined" component={Link} to={activityLogPath}>
            Activity Log
          </Button>
          <Button size="small" variant="outlined" component={Link} to={activityLauncherPath}>
            Activity Launcher
          </Button>
        </Stack>
      </Box>
      {err && <Alert severity="error" onClose={() => setErr('')}>{err}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={hasBadDates}
            helperText={hasBadDates ? 'End date must be on/after Start date' : ' '}
          />
          <TextField
            label="Status"
            select
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: { md: 280 } }}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading || hasBadDates}
          >
            {loading ? 'Loading…' : 'Apply / Refresh'}
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Chip label={`Total: ${items.length}`} variant="outlined" />
        <Chip label={`Submitted: ${statusCounts.submitted}`} color={statusCounts.submitted ? 'warning' : 'default'} variant={statusCounts.submitted ? 'filled' : 'outlined'} />
        <Chip label={`Returned: ${statusCounts.returned}`} color={statusCounts.returned ? 'default' : 'default'} variant="outlined" />
        <Chip label={`Finalized: ${statusCounts.finalized}`} color={statusCounts.finalized ? 'success' : 'default'} variant={statusCounts.finalized ? 'filled' : 'outlined'} />
        <Chip label={`Draft: ${statusCounts.draft}`} color={statusCounts.draft ? 'info' : 'default'} variant="outlined" />
      </Stack>

      <Divider />

      {loading && (
        <Box className="flex items-center justify-center gap-2 opacity-80 text-sm p-6">
          <CircularProgress size={18} /> Loading reviews…
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ opacity: 0.65 }}>
            No items match the filters.
          </Typography>
        </Paper>
      )}

      {!loading && items.length > 0 && (
        <Stack spacing={1.5}>
          {items.map((it, index) => {
            const key = it._safeId || `${it._safeInternName}-${it._safePeriodStart}-${index}`;
            const period = `${dateLabel(it._safePeriodStart)} → ${dateLabel(it._safePeriodEnd)}`;
            const reviewerLine = [
              it._safeSrReviewer ? `SR: ${it._safeSrReviewer}` : '',
              it._safeFacultyReviewer ? `Faculty: ${it._safeFacultyReviewer}` : '',
            ].filter(Boolean).join(' • ');

            return (
              <Paper
                key={key}
                variant="outlined"
                sx={{
                  p: 1.5,
                  cursor: it._safeId ? 'pointer' : 'default',
                  '&:hover': it._safeId ? { bgcolor: 'action.hover' } : undefined,
                }}
                role={it._safeId ? 'button' : undefined}
                tabIndex={it._safeId ? 0 : undefined}
                onClick={() => openReview(it)}
                onKeyDown={(event) => {
                  if (!it._safeId) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openReview(it);
                  }
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip size="small" label={it._safeStatusLabel} color={statusChipColor(it._safeStatus)} />
                      <Chip size="small" variant="outlined" label={`Avg ${it._safeAvg}`} />
                      {it._safeCompetencyCount > 0 && (
                        <Chip size="small" variant="outlined" label={`${it._safeCompetencyCount} competencies`} />
                      )}
                    </Stack>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {it._safeInternName || '—'}
                    </Typography>

                    <Typography variant="caption" sx={{ opacity: 0.72 }}>
                      Period: {period}
                    </Typography>

                    {reviewerLine && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.72 }}>
                        {reviewerLine}
                      </Typography>
                    )}

                    <Tooltip title={it._safeSummary || ''} disableHoverListener={!it._safeSummary || it._safeSummary.length < 120}>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.75,
                          opacity: 0.88,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {it._safeSummary}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      disabled={!it._safeId}
                      onClick={(event) => {
                        event.stopPropagation();
                        openReview(it);
                      }}
                    >
                      Open Review
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
